"use client";

import posthog from "posthog-js";
import { isStatisticsConsentGranted } from "@/legal/cookie-consent/useCookiebotConsent";
import { isProd } from "@/utils/environments";
import type { EcommerceItem } from "../e-commerce/item-schema";

export const isPostHogConfigured =
  !!process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN &&
  !!process.env.NEXT_PUBLIC_POSTHOG_HOST;

/**
 * Cookiebot is injected through Google Tag Manager, and the GTM container is
 * only configured for production. Outside production `window.Cookiebot` never
 * exists, so consent would always read as denied and PostHog would stay opted
 * out forever. Capturing is therefore allowed on non-production environments,
 * mirroring how Datadog handles tracking consent.
 */
export const isCaptureAllowed = (consentGranted: boolean): boolean =>
  consentGranted || !isProd();

/**
 * Forwards an already-enriched tracker payload to PostHog.
 * The payload is produced by `@cappelendamm/tracking`, so it carries the
 * session, page meta and campaign attribution the rest of the pipeline adds.
 *
 * Until the user grants Cookiebot "statistics" consent, PostHog runs in
 * cookieless mode and `beforeSend` drops these events, since the payload
 * carries the tracker's long-lived client id and order ids.
 */
export function capturePostHogEvent({
  event,
  ...properties
}: { event: string } & Record<string, unknown>): void {
  if (!isPostHogConfigured) return;
  for (const mappedEvent of mapToPostHogEcommerceEvents(event, properties)) {
    posthog.capture(mappedEvent.event, mappedEvent.properties);
  }
}

type EcommercePayload = {
  value?: number;
  currency?: string;
  transaction_id?: string;
  cart_id?: string;
  coupon?: string;
  campaign_source?: string;
  coupons?: string[];
  discount?: number;
  shipping?: number;
  tax?: number;
  payment_type?: string;
  shipping_tier?: string;
  item_list_id?: string;
  item_list_name?: string;
  promotion_id?: string;
  promotion_name?: string;
  creative_name?: string;
  creative_slot?: string;
  items?: EcommerceItem[];
};

type PostHogEvent = {
  event: string;
  properties: Record<string, unknown>;
};

const PRODUCT_EVENT_NAMES: Record<string, string> = {
  select_item: "Product Clicked",
  view_item: "Product Viewed",
  add_to_cart: "Product Added",
  remove_from_cart: "Product Removed",
};

const ECOMMERCE_EVENT_NAMES: Record<string, string> = {
  view_item_list: "Product List Viewed",
  view_promotion: "Promotion Viewed",
  select_promotion: "Promotion Clicked",
  view_cart: "Cart Viewed",
  begin_checkout: "Checkout Started",
  add_shipping_info: "Checkout Step Completed",
  add_payment_info: "Payment Info Entered",
  purchase: "Order Completed",
};

/**
 * PostHog funnels, breakdowns and revenue analytics filter on top-level
 * properties, while the GA4-shaped payload nests everything under
 * `ecommerce`. The nested object is kept as-is and the fields that matter for
 * conversion analysis are copied to the top level.
 */
export function withFlatEcommerceProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const ecommerce = properties.ecommerce as EcommercePayload | undefined;
  if (!ecommerce || typeof ecommerce !== "object") return properties;

  const items = Array.isArray(ecommerce.items) ? ecommerce.items : [];
  const flat = {
    value: ecommerce.value,
    currency: ecommerce.currency,
    transaction_id: ecommerce.transaction_id,
    cart_id: ecommerce.cart_id,
    coupon: ecommerce.coupon,
    campaign_source: ecommerce.campaign_source,
    coupons: ecommerce.coupons,
    discount: ecommerce.discount,
    shipping: ecommerce.shipping,
    tax: ecommerce.tax,
    payment_type: ecommerce.payment_type,
    shipping_tier: ecommerce.shipping_tier,
    item_count: items.length,
    item_ids: items.map((item) => item.item_id).filter(Boolean),
    item_categories: [
      ...new Set(items.map(getProductCategory).filter(Boolean)),
    ],
    item_product_types: [
      ...new Set(items.map((item) => item.item_category).filter(Boolean)),
    ],
  };

  return {
    ...Object.fromEntries(
      Object.entries(flat).filter(([, value]) => value !== undefined),
    ),
    ...properties,
  };
}

function withoutUndefined(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  );
}

function getProductCategory(item: EcommerceItem): string | undefined {
  return (
    item.item_category5 ??
    item.item_category4 ??
    item.item_category3 ??
    item.item_category2 ??
    item.item_category
  );
}

function mapProduct(item: EcommerceItem): Record<string, unknown> {
  const categoryPath = [
    item.item_category2,
    item.item_category3,
    item.item_category4,
    item.item_category5,
  ].filter((category): category is string => category !== undefined);

  return withoutUndefined({
    product_id: item.product_id ?? item.item_id,
    sku: item.item_id,
    product_type: item.item_category,
    category: getProductCategory(item),
    category_path: categoryPath.length > 0 ? categoryPath : undefined,
    name: item.item_name,
    brand: item.item_brand,
    variant: item.item_variant,
    price: item.price,
    quantity: item.quantity,
    coupon: item.coupon,
    position: item.index,
    image_url: item.image_url,
  });
}

export function mapToPostHogEcommerceEvents(
  event: string,
  properties: Record<string, unknown>,
): PostHogEvent[] {
  const ecommerce = properties.ecommerce as EcommercePayload | undefined;
  const baseProperties = withFlatEcommerceProperties(properties);

  if (event === "search") {
    return [
      {
        event: "Products Searched",
        properties: withoutUndefined({
          ...baseProperties,
          query: properties.search_term,
        }),
      },
    ];
  }

  if (!ecommerce || typeof ecommerce !== "object") {
    return [{ event, properties: baseProperties }];
  }

  const products = (ecommerce.items ?? []).map(mapProduct);
  const productEvent = PRODUCT_EVENT_NAMES[event];

  if (productEvent && ecommerce.items && ecommerce.items.length > 0) {
    return ecommerce.items.map((item) => {
      const product = mapProduct(item);
      const productValue =
        item.price === undefined
          ? ecommerce.value
          : item.price * (item.quantity ?? 1);

      return {
        event: productEvent,
        properties: withoutUndefined({
          ...baseProperties,
          ...product,
          currency: ecommerce.currency,
          value: productValue,
          cart_id: ecommerce.cart_id,
        }),
      };
    });
  }

  const mappedPropertiesByEvent: Record<string, Record<string, unknown>> = {
    view_item_list: {
      list_id: ecommerce.item_list_id,
      category: ecommerce.item_list_name,
      products,
    },
    view_promotion: {
      promotion_id: ecommerce.promotion_id,
      creative: ecommerce.creative_name,
      name: ecommerce.promotion_name,
      position: ecommerce.creative_slot,
    },
    select_promotion: {
      promotion_id: ecommerce.promotion_id,
      creative: ecommerce.creative_name,
      name: ecommerce.promotion_name,
      position: ecommerce.creative_slot,
    },
    view_cart: {
      cart_id: ecommerce.cart_id,
      products,
    },
    begin_checkout: {
      order_id: ecommerce.transaction_id ?? ecommerce.cart_id,
      value: ecommerce.value,
      shipping: ecommerce.shipping,
      tax: ecommerce.tax,
      discount: ecommerce.discount,
      coupon: ecommerce.coupon,
      currency: ecommerce.currency,
      products,
    },
    add_shipping_info: {
      checkout_id: ecommerce.cart_id,
      shipping_method: ecommerce.shipping_tier,
    },
    add_payment_info: {
      checkout_id: ecommerce.cart_id,
      order_id: ecommerce.transaction_id,
      payment_method: ecommerce.payment_type,
    },
    purchase: {
      checkout_id: ecommerce.cart_id,
      order_id: ecommerce.transaction_id,
      total: ecommerce.value,
      shipping: ecommerce.shipping,
      tax: ecommerce.tax,
      discount: ecommerce.discount,
      coupon: ecommerce.coupon,
      currency: ecommerce.currency,
      products,
    },
  };
  const mappedProperties = mappedPropertiesByEvent[event];
  const mappedEventName = ECOMMERCE_EVENT_NAMES[event];

  if (!mappedProperties || !mappedEventName) {
    return [{ event, properties: baseProperties }];
  }

  return [
    {
      event: mappedEventName,
      properties: withoutUndefined({
        ...baseProperties,
        ...mappedProperties,
      }),
    },
  ];
}

type PostHogUser = { id: string; email?: string; name?: string };

/**
 * The signed-in user, kept so they can be identified once consent arrives.
 * In cookieless mode PostHog captures without consent, so identifying before
 * opt-in would tie those anonymous events to the user's id, email and name.
 */
let signedInUser: PostHogUser | null = null;

function identifySignedInUser(): void {
  if (!signedInUser) return;
  posthog.identify(signedInUser.id, {
    $email: signedInUser.email,
    $name: signedInUser.name,
  });
}

/**
 * Aligns PostHog capturing and browser storage with the user's consent.
 * Opting in enables persistence (cookies/localStorage); opting out switches
 * PostHog to cookieless mode. Outside production consent is assumed granted,
 * see `isCaptureAllowed`.
 *
 * Opting in from cookieless mode resets PostHog's identity and the site sent
 * with flags, so both are restored after it.
 */
export function applyPostHogConsent(granted: boolean): void {
  if (!isPostHogConfigured) return;

  if (isCaptureAllowed(granted)) {
    if (!posthog.has_opted_in_capturing()) {
      posthog.opt_in_capturing({ captureEventName: false });
      setSiteForFlags();
      identifySignedInUser();
    }
    return;
  }

  if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
}

/**
 * Identifies the signed-in user, or defers it until consent is granted.
 */
export function identifyPostHogUser(user: PostHogUser): void {
  if (!isPostHogConfigured) return;
  signedInUser = user;
  if (posthog.has_opted_in_capturing()) identifySignedInUser();
}

/**
 * Lets feature flags target a site, with conditions such as
 * "$host = www.norskeserier.no". PostHog only stores the last site a person
 * was seen on, and nothing for anonymous visitors, so the current site is sent
 * with every flags request. Called from `loaded`, before the first request, so
 * it costs no extra request.
 */
export function setSiteForFlags(): void {
  if (!isPostHogConfigured) return;
  posthog.setPersonPropertiesForFlags(
    { $host: window.location.hostname },
    false,
  );
}

/**
 * Clears the identified user on logout.
 *
 * Without consent nothing was identified, and `posthog.reset()` would replace
 * the cookieless identity with a random id, so it is skipped.
 *
 * `posthog.reset()` also clears stored consent, which with
 * `opt_out_capturing_by_default` silently returns the instance to opted-out,
 * and the site sent with flags. Both must therefore be re-applied *after* the
 * reset, never before.
 */
export function resetPostHogIdentity(): void {
  if (!isPostHogConfigured) return;
  signedInUser = null;
  if (!posthog.has_opted_in_capturing()) return;
  posthog.reset();
  applyPostHogConsent(isStatisticsConsentGranted());
  setSiteForFlags();
}
